
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BillStatusResponse {
  success: boolean;
  error?: string;
  message?: string;
  details?: {
    raw_error: any;
    bill_check?: string;
  };
}

export function useBillStatusUpdate() {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<BillStatusResponse | null>(null);
  const { toast } = useToast();

  const checkBillExists = async (componentId: string) => {
    try {
      // First try to check by bill_component_id
      const { data: billByComponentId, error: componentIdError } = await supabase
        .from('bills')
        .select('id, status, subscription_id, bill_component_id')
        .eq('bill_component_id', componentId.trim())
        .maybeSingle();
        
      if (componentIdError) {
        console.error('Error checking bill by component_id:', componentIdError);
      }
      
      if (billByComponentId) {
        console.log('Found bill by bill_component_id:', billByComponentId);
        return billByComponentId;
      }
      
      // If not found, try by direct ID match
      const { data: billById, error: idError } = await supabase
        .from('bills')
        .select('id, status, subscription_id, bill_component_id')
        .eq('id', componentId.trim())
        .maybeSingle();
        
      if (idError) {
        console.error('Error checking bill by id:', idError);
      }
      
      if (billById) {
        console.log('Found bill by direct id match:', billById);
      }
      
      return billById;
    } catch (error) {
      console.error('Error in checkBillExists:', error);
      return null;
    }
  };

  const updateBillStatus = async (values: { billComponentId: string, action: string }) => {
    setLoading(true);
    setResponse(null);
    
    try {
      const { billComponentId, action } = values;
      
      if (!billComponentId.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please enter a valid Bill Component ID",
        });
        setLoading(false);
        return;
      }
      
      console.log(`Testing bill status update for bill component ${billComponentId} with action: ${action}`);
      
      // First check if bill exists in database for better debugging
      const billExists = await checkBillExists(billComponentId);
      
      if (billExists) {
        console.log(`Bill found in database: ${JSON.stringify(billExists)}`);
      } else {
        console.log(`Bill with component ID ${billComponentId} not found in database`);
        toast({
          variant: "destructive",
          title: "Warning",
          description: `Bill with component ID ${billComponentId} does not exist in database. API call may fail.`,
        });
      }
      
      // Make the API call to update the bill status
      const { data, error } = await supabase.functions.invoke('update-bill-status', {
        body: {
          billComponentId: billComponentId.trim(),
          action: action,
          apiKey: "test_payment_api_key" // Using test key for development environment
        }
      });
      
      if (error) {
        console.error('Bill status update error:', error);
        
        let errorMessage = "Failed to update bill status";
        let errorDetails: { raw_error: any; bill_check?: string } = { raw_error: error };
        
        if (error.message) {
          errorMessage = error.message;
          
          if (billExists) {
            errorDetails.bill_check = `Bill exists with status: ${billExists.status}`;
          } else {
            errorDetails.bill_check = 'Bill not found in database';
          }
        }
        
        setResponse({
          success: false,
          error: errorMessage,
          details: errorDetails
        });
        
        toast({
          variant: "destructive",
          title: "Error",
          description: errorMessage,
        });
        
        setLoading(false);
        return;
      }
      
      // Handle successful response
      setResponse(data);
      
      toast({
        title: "Success",
        description: data.message || `Bill status updated successfully`,
      });
    } catch (error: any) {
      console.error('Bill status update error:', error);
      
      setResponse({
        success: false,
        error: error.message || "Failed to update bill status",
        details: {
          raw_error: {
            name: error.name,
            stack: error.stack
          }
        }
      });
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update bill status",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    response,
    updateBillStatus
  };
}
